import { m, type Message } from '@/i18n';
import { type Level } from './constants';

/**
 * The channel law (docs/HEALTH_SIGNALS.md §6): what a severity GRADE does is
 * **platform-owned**, not configurable - authors own the mapping (which fact
 * means which grade), the platform owns what a grade costs, or over-alerting is
 * one config away. The three channels, ascending:
 *
 *   - **notice**  - dashboard-grade: visible where you already look; never
 *     enters the routed inbox, never pages a party.
 *   - **warning** - fold-grade: colours the dot, enters the counts, reaches the
 *     routed surfaces (the feed); never pages a party.
 *   - **critical** - page-grade: pushes to parties and subscribers. Must be
 *     rare - "late = critical" is declarable, but critical always costs a page,
 *     so crying wolf hurts the wolf-crier and the loop closes itself.
 *
 * `info` sits below notice (raw register facts) and is dashboard-only too.
 *
 * The law binds the SHARED channel: what a grade does to an item's parties. A
 * person's own subscription may page that person below critical (the
 * subscribed lane, src/domain/push.ts); who is paged at which grade is the
 * admission table in docs/NOTIFICATIONS.md "Web push".
 *
 * This is a CONSTANT keyed on the level alone - there is deliberately no setter,
 * no per-org override, no catalog field that changes it. A catalog may map a
 * fact to `critical` (author's choice); it may never make `critical` not page
 * or `warning` page (platform's law). That invariant is the whole point: the
 * channel behaviour cannot be tuned, only the grade assignment can.
 */
export interface Channel {
  /** Reaches the routed surfaces (the feed / fold): colours, counts, inbox. */
  routed: boolean;
  /** Page-grade: pushes to parties + subscribers. `critical` alone. */
  pushes: boolean;
}

export const CHANNEL_LAW: Record<Level, Channel> = {
  info: { routed: false, pushes: false }, // dashboard only (raw facts)
  notice: { routed: false, pushes: false }, // dashboard only
  warning: { routed: true, pushes: false }, // folds / colours / routed, never pages a party
  critical: { routed: true, pushes: true }, // pages parties + subscribers
};

/** The channel a grade rides. Platform-owned; see `CHANNEL_LAW`. */
export function channelFor(level: Level): Channel {
  return CHANNEL_LAW[level];
}

/** Page-grade? Only `critical` pushes (docs/HEALTH_SIGNALS.md §6). */
export function pushes(level: Level): boolean {
  return CHANNEL_LAW[level].pushes;
}

/** Reaches the routed inbox/fold (warning and above)? notice/info are
 *  dashboard-only. */
export function isRouted(level: Level): boolean {
  return CHANNEL_LAW[level].routed;
}

/**
 * A one-line, human statement of what a grade costs - the honesty gauge shown
 * where grades are authored (docs/HEALTH_SIGNALS.md §6, HS-D4): raising a
 * severity is never consequence-free, and the author sees the cost at the point
 * of choice. Note the scope: this describes the grade's platform channel for
 * ITEM-TRUTH signals (catalog tags routed to stakes). A LENS-local board rule
 * (docs/HEALTH_SIGNALS.md §3) colours only its own board and pages nobody, so
 * its editor phrases the cost as board-local intensity, not a page.
 */
export const CHANNEL_MEANING: Record<Level, Message> = {
  info: m('flows.channel.info'),
  notice: m('flows.channel.notice'),
  warning: m('flows.channel.warning'),
  critical: m('flows.channel.critical'),
};
